import debug from 'debug';
import Debounce from 'Global/Shared/Components/Debounce/Debounce';
import Breakpoints from 'Minetilbud/Shared/Components/Breakpoints/Breakpoints';
import Spinner from 'Minetilbud/Shared/Components/spinner/spinner';
import Customers from '../data/customers';
import Templates from '../templates/active-customers-templates';
import Image from '../utils/image';

const log = debug('FrontPageCatalogs:Events:CustomerLoader');
const breakpoints = new Breakpoints();
const debounce = new Debounce().debounce;
const spinner = new Spinner();
const templates = new Templates();

class CustomerLoader {
	constructor(element) {
		this.element = element;

		this.resizeEvent = this.resizeHandler.bind(this);
		this.scrollEvent = this.scrollHandler.bind(this);

		this.resizeHandler = debounce(this.resizeHandler, 250);
		this.scrollHandler = debounce(this.scrollHandler, 250);

		this.siteWidth = this.element.getBoundingClientRect().width;

		this.initialize();
	}

	eventListeners() {
		document.addEventListener('scroll', this.scrollEvent, true);
		window.addEventListener('resize', this.resizeEvent, false);
	}

	getActiveCatalog(customer) {
		const feedCatalogs = customer.catalogs.filter(catalog => catalog.isFeed === true);
		const paperCatalogs = customer.catalogs.filter(catalog => catalog.isFeed === false);

		if (paperCatalogs.length > 0) {
			return paperCatalogs[0];
		}

		return feedCatalogs[0];
	}

	getCustomerElementsInView() {
		const customerElementsInView = [];
		const elements = Array.from(this.element.children);

		elements.forEach(element => {
			const elementRect = element.getBoundingClientRect();

			if ((elementRect.top > 0 || elementRect.bottom > 0) && elementRect.top < window.screen.height) {
				customerElementsInView.push(element);
			};
		})

		return customerElementsInView;
	}

	/**
	 * @param {string} imageUrl
	 * @param {boolean} isPriority
	 * 
	 * @returns {Promise} image
	 */
	 async getImage(imageUrl) {
		return new Promise((resolve, reject) => {
			const imageSize = Image.getSize();
			const url = Image.getUrl({ 
				imageUrl,
				imageSize
			});

			const image = document.createElement('img');
	
			image.onload = () => {
				resolve(image);
			}

			image.onerror = (error) => {
				resolve(null);
			}
			
			image.src = url;
		});
	}

	async initialize() {
		const dictionary = await templates.dictionaryInit();

		if (breakpoints.MediaBreakpointUp('xs')) {
			this.device = 'large';
		} else {
			this.device = 'small';
		}

		this.eventListeners();
		this.setup();
	}

	/**
	 * @param {any} id
	 * 
	 * @returns {boolean}
	 */
	 isLoaded(id) {
		if (this.loaded !== undefined && this.loaded.indexOf(id) > -1) return true;
		if (this.loaded === undefined) this.loaded = [];
		
		this.loaded.push(id);

		return false
	}

	async render(customer, customerElement) {
		let verifiedImage;
		const linkElement = customerElement.querySelector('[data-event="catalog-link"]');
		if (linkElement === null) return;

		linkElement.innerHTML = '';
		linkElement.insertAdjacentHTML('beforeend', spinner.use('o-front-page-catalogs-customer__spinner'));

		const catalog = this.getActiveCatalog(customer);
		let catalogImage = await this.getImage(catalog.frontPageImageUrl);
		
		if (catalogImage === null) {
			catalogImage = Image.getCircleImage(customer.circleLogoUrl);

			catalogImage.setAttribute('width', catalogImage.width);
			catalogImage.setAttribute('height', catalogImage.height);
		} else {
			verifiedImage = Image.verifySize(catalogImage);

			catalogImage.setAttribute('width', verifiedImage.width);
			catalogImage.setAttribute('height', verifiedImage.height);
		}

		catalogImage.setAttribute('data-id', customer.id);
		catalogImage.setAttribute('data-js', 'CustomerCatalogsImageLoader');
		catalogImage.setAttribute('class', 'o-front-page-catalogs-customer__image');
		catalogImage.setAttribute('alt', `${customer.name}s tilbudsavis`);

		linkElement.innerHTML = '';
		linkElement.insertAdjacentHTML('beforeend', templates.activeCustomerContent(customer));

		const imageWrapper = linkElement.querySelector('[data-js="image-wrapper"]');
		imageWrapper.appendChild(catalogImage);
	}

	reset() {
		this.loaded = [];
		this.setup();
	}

	resizeHandler() {
		const newWidth = this.element.getBoundingClientRect().width;
		if (this.siteWidth === newWidth) return;
		this.siteWidth = newWidth;

		if (breakpoints.MediaBreakpointUp('xs')) {
			if (this.device === 'small') {
				this.device = 'large';
				this.loaded = [];
			}
		} else {
			if (this.device === 'large') {
				this.device = 'small';
				this.loaded = [];
			}
		}
		
		this.setup();
	}

	scrollHandler() {
		this.setup();
	}

	setup() {
		const customerElementsInView = this.getCustomerElementsInView();
		if (customerElementsInView.length === 0) return;

		customerElementsInView.forEach(element => {
			const customerId = element.getAttribute('data-key');
			if (this.isLoaded(customerId)) return;

			const selectedCustomers = Customers.all.filter(customer => customer.id === customerId);
			if (selectedCustomers.length === 0) return;

			this.render(selectedCustomers[0], element);
		});
	}
}

export default CustomerLoader;
